// src/pages/reports/Drm_conditional.tsx
import React, { useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Button,
  TextField,
  Pagination,
  Chip,
  IconButton,
  Tooltip,
  Avatar
} from "@mui/material";
import {
  Sync as SyncIcon,
  CloudUpload as ImportIcon,
  GetApp as ExportIcon,
  PowerSettingsNew as ActivateIcon,
  PowerOff as DeactivateIcon,
  Search as SearchIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  Event as CalendarIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
import { authenticationService } from "src/services/authentication.service";
import { hasPermission } from "src/components/Permissions/hasPermission";
import dayjs from 'dayjs';
import reportDownloadService from 'src/services/reportDownload.service';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.locale('en-gb');

// Mock Data with new fields - UPDATED dates to DD/MM/YYYY format
const mockData = [
  {
    id: 1,
    stbDetails: "STB-789XYZ (MAC: AB:CD:EF:12:34)",
    boxStatus: "Active",
    firstActivation: "15/01/2023", // CHANGED: from 2023-01-15
    subscriptionExpiry: "14/01/2024 (30 days left)", // CHANGED: from 2024-01-14
    subscriberStats: "Active: 1,250 | Inactive: 320",
    drmVersion: "DRM 3.2.1 (Compliant)",
    expiringSubs: "5 channels, 2 bouquets",
    freshActivations: 12,
    deactivations: 5,
    reactivations: 3,
    channelChanges: "+3 added, -2 removed",
    subscriberStatus: "Active",
    backupSync: "Synced",
    logTimestamp: "20/04/2023 14:30", // CHANGED: from 2023-04-20 14:30
    dataSynced: "Complete",
    securityCompliance: "Compliant",
    logIntegrity: "Secure",
    activationSource: "SMS",
    networkSupport: "Two-way"
  },
  {
    id: 2,
    stbDetails: "STB-456ABC (MAC: BC:DE:FA:23:45)",
    boxStatus: "Inactive",
    firstActivation: "10/03/2023", // CHANGED: from 2023-03-10
    subscriptionExpiry: "31/12/2023 (7 days left)", // CHANGED: from 2023-12-31
    subscriberStats: "Active: 980 | Inactive: 150",
    drmVersion: "DRM 3.1.5 (Non-Compliant)",
    expiringSubs: "3 channels, 1 bouquet",
    freshActivations: 8,
    deactivations: 3,
    reactivations: 2,
    channelChanges: "+2 added, -1 removed",
    subscriberStatus: "Inactive",
    backupSync: "Not Synced",
    logTimestamp: "19/04/2023 10:15", // CHANGED: from 2023-04-19 10:15
    dataSynced: "Partial",
    securityCompliance: "Non-Compliant",
    logIntegrity: "Compromised",
    activationSource: "DRM",
    networkSupport: "One-way"
  },
  {
    id: 3,
    stbDetails: "STB-123DEF (MAC: CD:EF:12:34:56)",
    boxStatus: "Active",
    firstActivation: "28/02/2023", // CHANGED: from 2023-02-28
    subscriptionExpiry: "30/06/2024 (180 days left)", // CHANGED: from 2024-06-30
    subscriberStats: "Active: 2,100 | Inactive: 450",
    drmVersion: "DRM 3.3.0 (Compliant)",
    expiringSubs: "8 channels, 3 bouquets",
    freshActivations: 15,
    deactivations: 7,
    reactivations: 4,
    channelChanges: "+5 added, -3 removed",
    subscriberStatus: "Active",
    backupSync: "Synced",
    logTimestamp: "21/04/2023 09:45", // CHANGED: from 2023-04-21 09:45
    dataSynced: "Complete",
    securityCompliance: "Compliant",
    logIntegrity: "Secure",
    activationSource: "SMS",
    networkSupport: "Two-way"
  },
  {
    id: 4,
    stbDetails: "STB-789GHI (MAC: DE:FA:23:45:67)",
    boxStatus: "Active",
    firstActivation: "05/01/2023", // CHANGED: from 2023-01-05
    subscriptionExpiry: "15/11/2023 (45 days left)", // CHANGED: from 2023-11-15
    subscriberStats: "Active: 1,750 | Inactive: 280",
    drmVersion: "DRM 3.2.0 (Compliant)",
    expiringSubs: "6 channels, 2 bouquets",
    freshActivations: 10,
    deactivations: 4,
    reactivations: 3,
    channelChanges: "+4 added, -1 removed",
    subscriberStatus: "Active",
    backupSync: "Synced",
    logTimestamp: "20/04/2023 16:20", // CHANGED: from 2023-04-20 16:20
    dataSynced: "Complete",
    securityCompliance: "Compliant",
    logIntegrity: "Secure",
    activationSource: "SMS",
    networkSupport: "Two-way"
  },
  {
    id: 5,
    stbDetails: "STB-456JKL (MAC: EF:12:34:56:78)",
    boxStatus: "Inactive",
    firstActivation: "01/04/2023", // CHANGED: from 2023-04-01
    subscriptionExpiry: "31/10/2023 (90 days left)", // CHANGED: from 2023-10-31
    subscriberStats: "Active: 1,100 | Inactive: 200",
    drmVersion: "DRM 3.2.2 (Compliant)",
    expiringSubs: "4 channels, 1 bouquet",
    freshActivations: 6,
    deactivations: 2,
    reactivations: 1,
    channelChanges: "+1 added, -0 removed",
    subscriberStatus: "Inactive",
    backupSync: "Synced",
    logTimestamp: "21/04/2023 14:10", // CHANGED: from 2023-04-21 14:10
    dataSynced: "Complete",
    securityCompliance: "Compliant",
    logIntegrity: "Secure",
    activationSource: "DRM",
    networkSupport: "One-way"
  }
];

// Define columns for the report download service
const columns = [
  { field: 'stbDetails', headerName: 'STB/Consumer Details', minWidth: 200 },
  { field: 'boxStatus', headerName: 'Box Status', minWidth: 100 },
  { field: 'firstActivation', headerName: 'First Activation Date', minWidth: 130 },
  { field: 'subscriptionExpiry', headerName: 'STB/unique consumer subscription expiring detail', minWidth: 250 },
  { field: 'subscriberStats', headerName: 'Total active and inactive subscriber\'s', minWidth: 180 },
  { field: 'drmVersion', headerName: 'DRM version currently in use', minWidth: 200 },
  { field: 'expiringSubs', headerName: 'Total number of à-la-carte channels and bouquets', minWidth: 200 },
  { field: 'freshActivations', headerName: 'Today\'s fresh activation count', minWidth: 150, type: 'number' },
  { field: 'deactivations', headerName: 'De-activation count', minWidth: 130, type: 'number' },
  { field: 'reactivations', headerName: 'Re-activation count', minWidth: 130, type: 'number' },
  { field: 'channelChanges', headerName: 'à-la-carte channels and bouquets addition/removal count', minWidth: 250 },
  { field: 'backupSync', headerName: 'Backup server synchronization status', minWidth: 200 },
  { field: 'logTimestamp', headerName: 'Log timestamp with date and time', minWidth: 180 },
  { field: 'dataSynced', headerName: 'Data synchronization status', minWidth: 180 },
  { field: 'securityCompliance', headerName: 'Security compliance status', minWidth: 180 },
  { field: 'logIntegrity', headerName: 'Log integrity status', minWidth: 150 },
  { field: 'activationSource', headerName: 'STB activation source (SMS/DRM)', minWidth: 200 },
  { field: 'networkSupport', headerName: 'Network support type', minWidth: 150 },
];

const Drm_conditional = () => {
  const [selectAll, setSelectAll] = useState(false);
  const [selectiveBoxes, setSelectiveBoxes] = useState(false);
  const [singleBox, setSingleBox] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const rowsPerPage = 5;
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  // Filter data based on search term
  const filteredData = mockData.filter((row) =>
    Object.values(row).some(
      (val) => val.toString().toLowerCase().includes(searchTerm.toLowerCase())
  ));

  // Pagination
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Table headers with full text and display text
  const headers = [
    { id: 'stbDetails', display: 'STB/Consumer', full: 'STB/Consumer Details' },
    { id: 'boxStatus', display: 'Box Status', full: 'Box Status' },
    { id: 'firstActivation', display: 'First Activation', full: 'First Activation Date' },
    { id: 'subscriptionExpiry', display: 'Subscription Expiry', full: 'STB/unique consumer subscription expiring detail' },
    { id: 'subscriberStats', display: 'Subscribers', full: 'Total active and inactive subscriber\'s' },
    { id: 'drmVersion', display: 'DRM Version', full: 'DRM version currently in use' },
    { id: 'expiringSubs', display: 'Expiring Subs', full: 'Total number of à-la-carte channels and bouquets' },
    { id: 'freshActivations', display: 'Fresh Activations', full: 'Today\'s fresh activation count' },
    { id: 'deactivations', display: 'De-activations', full: 'De-activation count' },
    { id: 'reactivations', display: 'Re-activations', full: 'Re-activation count' },
    { id: 'channelChanges', display: 'Channel Changes', full: 'à-la-carte channels and bouquets addition/removal count' },
    { id: 'backupSync', display: 'Backup Sync', full: 'Backup server synchronization status' },
    { id: 'logTimestamp', display: 'Log Time', full: 'Log timestamp with date and time' },
    { id: 'dataSynced', display: 'Data Sync', full: 'Data synchronization status' },
    { id: 'securityCompliance', display: 'Security', full: 'Security compliance status' },
    { id: 'logIntegrity', display: 'Log Integrity', full: 'Log integrity status' },
    { id: 'activationSource', display: 'Activation Source', full: 'STB activation source (SMS/DRM)' },
    { id: 'networkSupport', display: 'Network', full: 'Network support type' },
  ];

  // Single download handler for both formats using common service
  const handleDownload = async (format: 'pdf' | 'excel') => {
    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      // Generate custom filename
      const customFileName = `DRM_Conditional_Access_Report_${dayjs().format('YYYY-MM-DD_HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const success = await reportDownloadService.downloadReport({
        title: 'DRM Conditional Access Report',
        columns,
        rows: filteredData,
        fileName: customFileName,

        // Excel specific configuration - using full headers
        excelHeaders: headers.map(header => header.full),
        excelRowMapper: (row: any) => [
          row.stbDetails || '',
          row.boxStatus || '',
          row.firstActivation || '',
          row.subscriptionExpiry || '',
          row.subscriberStats || '',
          row.drmVersion || '',
          row.expiringSubs || '',
          row.freshActivations || 0,
          row.deactivations || 0,
          row.reactivations || 0,
          row.channelChanges || '',
          row.backupSync || '',
          row.logTimestamp || '',
          row.dataSynced || '',
          row.securityCompliance || '',
          row.logIntegrity || '',
          row.activationSource || '',
          row.networkSupport || ''
        ]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  return (
    <Box sx={{ margin: "20px 30px 0px 20px", overflow: "hidden" }}>
      {/* Header Section */}
      <Box sx={{ margin: "0px 0px 20px 0px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">DRM Conditional Access</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {hasPermission(["OWNER"]) && (
            <>
              <Button 
                variant="contained" 
                startIcon={<ImportIcon />}
                sx={{ textTransform: 'none' }}
              >
                Import File
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<ExportIcon />}
                onClick={() => handleDownload('excel')}
                disabled={downloading.excel}
                sx={{ textTransform: 'none' }}
              >
                {downloading.excel ? 'Generating...' : 'Export Excel'}
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload('pdf')}
                disabled={downloading.pdf}
                sx={{ textTransform: 'none' }}
              >
                {downloading.pdf ? 'Generating...' : 'Export PDF'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Controls Section */}
      <Box sx={{ 
        display: "flex", 
        flexDirection: { xs: "column", md: "row" }, 
        gap: 2, 
        mb: 3,
        alignItems: { xs: "flex-start", md: "center" }
      }}>
        {/* Search Box */}
        <TextField
          size="small"
          placeholder="Search..."
          InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          sx={{ width: { xs: "100%", md: "300px" } }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* Box Selection */}
        <Box sx={{ 
          display: "flex", 
          flexWrap: "wrap", 
          gap: 1, 
          backgroundColor: "action.hover",
          p: 1,
          borderRadius: 1
        }}>
          <Typography variant="body1" sx={{ alignSelf: "center", mr: 1 }}>
            Box Selection:
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={selectAll} onChange={(e) => setSelectAll(e.target.checked)} />}
            label="All"
          />
          <FormControlLabel
            control={<Checkbox checked={selectiveBoxes} onChange={(e) => setSelectiveBoxes(e.target.checked)} />}
            label="Selective"
          />
          <FormControlLabel
            control={<Checkbox checked={singleBox} onChange={(e) => setSingleBox(e.target.checked)} />}
            label="Single"
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
          <Tooltip title="Activate Selected">
            <IconButton color="success">
              <ActivateIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deactivate Selected">
            <IconButton color="error">
              <DeactivateIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Force Sync">
            <IconButton color="info">
              <SyncIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "auto" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 250px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="50px">
                  <Checkbox />
                </TableCell>
                {headers.map((header) => (
                  <Tooltip key={header.id} title={header.full} arrow>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: '120px',
                      maxWidth: '200px'
                    }}>
                      {header.display}
                    </TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell><Checkbox /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: "primary.main" }}>S</Avatar>
                      <Typography variant="body2">{row.stbDetails}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.boxStatus} 
                      size="small" 
                      color={row.boxStatus === "Active" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>{row.firstActivation}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CalendarIcon color="action" fontSize="small" />
                      <Typography variant="body2">
                        {row.subscriptionExpiry.split(" (")[0]}
                        <Typography component="span" variant="caption" color={
                          row.subscriptionExpiry.includes("30 days") ? "warning.main" :
                          row.subscriptionExpiry.includes("7 days") ? "error.main" :
                          "success.main"
                        }>
                          {" (" + row.subscriptionExpiry.split(" (")[1]}
                        </Typography>
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Chip label={row.subscriberStats.split("|")[0]} color="success" size="small" />
                      <Chip label={row.subscriberStats.split("|")[1]} color="error" size="small" />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {row.drmVersion.includes("Compliant") ? (
                        <VerifiedIcon color="success" fontSize="small" />
                      ) : (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                      <Typography variant="body2">{row.drmVersion}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.expiringSubs}</TableCell>
                  <TableCell>{row.freshActivations}</TableCell>
                  <TableCell>{row.deactivations}</TableCell>
                  <TableCell>{row.reactivations}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Chip label={row.channelChanges.split(", ")[0]} color="success" size="small" />
                      <Chip label={row.channelChanges.split(", ")[1]} color="error" size="small" />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {row.backupSync}
                      {row.backupSync === "Synced" ? (
                        <SyncIcon fontSize="small" color="success" />
                      ) : (
                        <SyncIcon fontSize="small" color="error" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.logTimestamp}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.dataSynced} 
                      size="small" 
                      color={row.dataSynced === "Complete" ? "success" : "warning"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.securityCompliance} 
                      size="small" 
                      color={row.securityCompliance === "Compliant" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.logIntegrity} 
                      size="small" 
                      color={row.logIntegrity === "Secure" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>{row.activationSource}</TableCell>
                  <TableCell>{row.networkSupport}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default Drm_conditional;